import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Briefcase,
  Calendar,
  DollarSign,
  Clock,
  Upload,
  FileText,
  Image,
  File,
  Trash2,
  Plus,
  MessageSquare,
  Download,
  ExternalLink,
  Loader2,
  X,
  Eye,
  Pencil,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import FileViewer from '../components/FileViewer';

const JobDetailPage = () => {
  const { jobId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [notes, setNotes] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editJobDialogOpen, setEditJobDialogOpen] = useState(false);
  const [editTimeDialogOpen, setEditTimeDialogOpen] = useState(false);
  
  // File viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Form states
  const [newTimeEntry, setNewTimeEntry] = useState({ hours: '', description: '', date: '' });
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit job form
  const [editJobForm, setEditJobForm] = useState({
    title: '',
    description: '',
    budget: '',
    due_date: '',
    job_type: ''
  });
  const [customers, setCustomers] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);

  const handleViewFile = (file) => {
    setSelectedFile(file);
    setViewerOpen(true);
  };

  const fetchJob = useCallback(async () => {
    try {
      const [jobRes, notesRes, timeRes, expensesRes, customersRes, typesRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs/${jobId}/notes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/time-entries?job_id=${jobId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/expenses?job_id=${jobId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/job-types`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setJob(jobRes.data);
      setNotes(notesRes.data);
      setTimeEntries(timeRes.data);
      setExpenses(expensesRes.data);
      setCustomers(customersRes.data);
      setJobTypes(typesRes.data.job_types);
      setNewStatus(jobRes.data.status);
      
      // Initialize edit form with current job data
      setEditJobForm({
        title: jobRes.data.title || '',
        description: jobRes.data.description || '',
        budget: jobRes.data.budget || '',
        due_date: jobRes.data.due_date || '',
        job_type: jobRes.data.job_type || '',
        customer_id: jobRes.data.customer_id || ''
      });
    } catch (err) {
      toast.error('Failed to load job');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  }, [jobId, token, navigate]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const handleAddTimeEntry = async () => {
    if (!newTimeEntry.hours) {
      toast.error('Please enter hours');
      return;
    }
    try {
      await axios.post(`${API}/time-entries`, {
        job_id: jobId,
        hours: parseFloat(newTimeEntry.hours),
        description: newTimeEntry.description,
        date: newTimeEntry.date || new Date().toISOString().split('T')[0]
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Time entry added');
      setTimeDialogOpen(false);
      setNewTimeEntry({ hours: '', description: '', date: '' });
      fetchJob();
    } catch (err) {
      toast.error('Failed to add time entry');
    }
  };

  const handleEditTimeEntry = (entry) => {
    setEditingTimeEntry({
      ...entry,
      hours: entry.hours.toString(),
      date: entry.date
    });
    setEditTimeDialogOpen(true);
  };

  const handleUpdateTimeEntry = async () => {
    if (!editingTimeEntry?.hours) {
      toast.error('Please enter hours');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/time-entries/${editingTimeEntry.id}`, {
        hours: parseFloat(editingTimeEntry.hours),
        description: editingTimeEntry.description,
        date: editingTimeEntry.date
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Time entry updated');
      setEditTimeDialogOpen(false);
      setEditingTimeEntry(null);
      fetchJob();
    } catch (err) {
      toast.error('Failed to update time entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTimeEntry = async (entryId) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await axios.delete(`${API}/time-entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Time entry deleted');
      fetchJob();
    } catch (err) {
      toast.error('Failed to delete time entry');
    }
  };

  const handleEditJob = async () => {
    if (!editJobForm.title) {
      toast.error('Please enter a job title');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/jobs/${jobId}`, {
        title: editJobForm.title,
        description: editJobForm.description,
        budget: editJobForm.budget ? parseFloat(editJobForm.budget) : null,
        due_date: editJobForm.due_date || null,
        job_type: editJobForm.job_type,
        customer_id: editJobForm.customer_id
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Job updated');
      setEditJobDialogOpen(false);
      fetchJob();
    } catch (err) {
      toast.error('Failed to update job');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    try {
      await axios.post(`${API}/jobs/${jobId}/notes`, {
        job_id: jobId,
        content: newNote
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Note added');
      setNoteDialogOpen(false);
      setNewNote('');
      fetchJob();
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await axios.put(`${API}/jobs/${jobId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      setStatusDialogOpen(false);
      fetchJob();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of files) {
        // Get signature from backend
        const resourceType = file.type.startsWith('image/') ? 'image' : 'raw';
        const sigRes = await axios.get(
          `${API}/cloudinary/signature?resource_type=${resourceType}&folder=uh_communications/jobs/${job.job_number}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const sig = sigRes.data;

        // Upload to Cloudinary
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', sig.api_key);
        formData.append('timestamp', sig.timestamp);
        formData.append('signature', sig.signature);
        formData.append('folder', sig.folder);

        const cloudinaryRes = await axios.post(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
          formData
        );

        // Save file reference to job
        await axios.post(`${API}/jobs/${jobId}/files`, {
          url: cloudinaryRes.data.secure_url,
          public_id: cloudinaryRes.data.public_id,
          filename: file.name,
          resource_type: resourceType,
          format: cloudinaryRes.data.format
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      
      toast.success('Files uploaded successfully');
      setUploadDialogOpen(false);
      fetchJob();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted');
      fetchJob();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleDeleteJob = async () => {
    if (!window.confirm(`Are you sure you want to delete job ${job.job_number}? This will also delete all related time entries, expenses, and notes. This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job deleted successfully');
      navigate('/jobs');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete job');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      review: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      on_hold: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getFileIcon = (file) => {
    if (file.resource_type === 'image') return <Image className="w-5 h-5" />;
    if (file.format === 'pdf') return <FileText className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-lg text-[#1F3C4F] font-semibold" data-testid="job-number">
                {job.job_number}
              </span>
              <Badge className={getStatusBadge(job.status)}>
                {job.status?.replace('_', ' ')}
              </Badge>
            </div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">{job.title}</h1>
            <p className="text-slate-500 mt-1">{job.customer_name} • {job.job_type}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={editJobDialogOpen} onOpenChange={setEditJobDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="edit-job-btn">
                <Pencil className="w-4 h-4 mr-2" />
                Edit Job
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Edit Job</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Job Title *</Label>
                  <Input
                    id="edit-title"
                    value={editJobForm.title}
                    onChange={(e) => setEditJobForm({ ...editJobForm, title: e.target.value })}
                    data-testid="edit-job-title"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Job Type</Label>
                    <Select 
                      value={editJobForm.job_type}
                      onValueChange={(value) => setEditJobForm({ ...editJobForm, job_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Customer</Label>
                    <Select 
                      value={editJobForm.customer_id}
                      onValueChange={(value) => setEditJobForm({ ...editJobForm, customer_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name} {customer.company && `(${customer.company})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-budget">Budget</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="edit-budget"
                        type="number"
                        placeholder="0.00"
                        className="pl-9"
                        value={editJobForm.budget}
                        onChange={(e) => setEditJobForm({ ...editJobForm, budget: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-due-date">Due Date</Label>
                    <Input
                      id="edit-due-date"
                      type="date"
                      value={editJobForm.due_date}
                      onChange={(e) => setEditJobForm({ ...editJobForm, due_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    rows={4}
                    value={editJobForm.description}
                    onChange={(e) => setEditJobForm({ ...editJobForm, description: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditJobDialogOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleEditJob} 
                  disabled={saving}
                  className="bg-[#1F3C4F] hover:bg-[#152a38]"
                  data-testid="save-job-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="change-status-btn">Change Status</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Update Job Status</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateStatus} className="bg-[#1F3C4F] hover:bg-[#152a38]">
                  Update Status
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button 
            variant="outline" 
            onClick={handleDeleteJob}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            data-testid="delete-job-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Job
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#1F3C4F]/10 rounded-xl">
              <Clock className="w-5 h-5 text-[#1F3C4F]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Hours</p>
              <p className="text-xl font-heading font-bold">{job.total_hours?.toFixed(1) || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#FF1493]/10 rounded-xl">
              <DollarSign className="w-5 h-5 text-[#FF1493]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Expenses</p>
              <p className="text-xl font-heading font-bold">{formatCurrency(job.total_expenses)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#FFB800]/10 rounded-xl">
              <DollarSign className="w-5 h-5 text-[#FFB800]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Budget</p>
              <p className="text-xl font-heading font-bold">{job.budget ? formatCurrency(job.budget) : '-'}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#1F3C4F]/10 rounded-xl">
              <Calendar className="w-5 h-5 text-[#1F3C4F]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Due Date</p>
              <p className="text-xl font-heading font-bold">
                {job.due_date ? new Date(job.due_date).toLocaleDateString() : '-'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="files">Files ({job.files?.length || 0})</TabsTrigger>
          <TabsTrigger value="time">Time ({timeEntries.length})</TabsTrigger>
          <TabsTrigger value="expenses">Expenses ({expenses.length})</TabsTrigger>
          <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Job Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none">
                {job.description ? (
                  <p className="text-slate-600">{job.description}</p>
                ) : (
                  <p className="text-slate-400 italic">No description provided</p>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Created</span>
                  <p className="font-medium">{new Date(job.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-slate-500">Job Type</span>
                  <p className="font-medium">{job.job_type}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="files" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Files</CardTitle>
              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1F3C4F] hover:bg-[#152a38]" data-testid="upload-file-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Files
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload Files</DialogTitle>
                  </DialogHeader>
                  <div 
                    className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                  >
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="w-8 h-8 animate-spin text-[#1F3C4F]" />
                        <p className="mt-2 text-slate-600">Uploading...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 mx-auto text-slate-400" />
                        <p className="mt-2 text-slate-600">Drag & drop files here or</p>
                        <label className="mt-2 cursor-pointer text-[#1F3C4F] hover:underline">
                          browse files
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                            data-testid="file-input"
                          />
                        </label>
                        <p className="mt-2 text-xs text-slate-400">Supports images, PDFs, documents</p>
                      </>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {job.files?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {job.files.map((file) => (
                    <div 
                      key={file.id}
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-[#1F3C4F]/20 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => handleViewFile(file)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          {getFileIcon(file)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{file.filename}</p>
                          <p className="text-xs text-slate-500 capitalize">{file.resource_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleViewFile(file); }}
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleDeleteFile(file.id); }}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <File className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>No files uploaded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Time Entries</CardTitle>
              <Dialog open={timeDialogOpen} onOpenChange={setTimeDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1F3C4F] hover:bg-[#152a38]" data-testid="add-time-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Log Time
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Log Time</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="hours">Hours *</Label>
                        <Input
                          id="hours"
                          type="number"
                          step="0.5"
                          placeholder="0.0"
                          value={newTimeEntry.hours}
                          onChange={(e) => setNewTimeEntry({ ...newTimeEntry, hours: e.target.value })}
                          data-testid="time-hours-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="time-date">Date</Label>
                        <Input
                          id="time-date"
                          type="date"
                          value={newTimeEntry.date}
                          onChange={(e) => setNewTimeEntry({ ...newTimeEntry, date: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="time-desc">Description</Label>
                      <Textarea
                        id="time-desc"
                        placeholder="What did you work on?"
                        value={newTimeEntry.description}
                        onChange={(e) => setNewTimeEntry({ ...newTimeEntry, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setTimeDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddTimeEntry} className="bg-[#1F3C4F] hover:bg-[#152a38]" data-testid="time-submit-btn">
                      Add Entry
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {timeEntries.length > 0 ? (
                <div className="space-y-3">
                  {timeEntries.map((entry) => (
                    <div 
                      key={entry.id}
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                    >
                      <div>
                        <p className="font-medium">{entry.description || 'No description'}</p>
                        <p className="text-sm text-slate-500">
                          {entry.user_name} • {new Date(entry.date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-lg font-heading font-bold text-[#1F3C4F]">{entry.hours} hrs</p>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditTimeEntry(entry)}
                          className="text-slate-400 hover:text-[#1F3C4F]"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteTimeEntry(entry.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>No time entries yet</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Edit Time Entry Dialog */}
          <Dialog open={editTimeDialogOpen} onOpenChange={(open) => { setEditTimeDialogOpen(open); if (!open) setEditingTimeEntry(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Time Entry</DialogTitle>
              </DialogHeader>
              {editingTimeEntry && (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-hours">Hours *</Label>
                      <Input
                        id="edit-hours"
                        type="number"
                        step="0.5"
                        value={editingTimeEntry.hours}
                        onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, hours: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-time-date">Date</Label>
                      <Input
                        id="edit-time-date"
                        type="date"
                        value={editingTimeEntry.date}
                        onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-time-desc">Description</Label>
                    <Textarea
                      id="edit-time-desc"
                      value={editingTimeEntry.description || ''}
                      onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditTimeDialogOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleUpdateTimeEntry} 
                  disabled={saving}
                  className="bg-[#1F3C4F] hover:bg-[#152a38]"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="expenses" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Expenses</CardTitle>
              <Link to={`/expenses?job_id=${jobId}`}>
                <Button className="bg-[#1F3C4F] hover:bg-[#152a38]">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Expense
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {expenses.length > 0 ? (
                <div className="space-y-3">
                  {expenses.map((expense) => (
                    <div 
                      key={expense.id}
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg"
                    >
                      <div>
                        <p className="font-medium capitalize">{expense.category?.replace('_', ' ')}</p>
                        <p className="text-sm text-slate-500">
                          {expense.description || 'No description'} • {new Date(expense.date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-heading font-bold text-[#FF1493]">
                          {formatCurrency(expense.amount)}
                        </p>
                        {expense.miles && (
                          <p className="text-sm text-slate-500">{expense.miles} miles</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <DollarSign className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>No expenses recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Notes</CardTitle>
              <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1F3C4F] hover:bg-[#152a38]" data-testid="add-note-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Note
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Note</DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <Textarea
                      placeholder="Write your note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={5}
                      data-testid="note-content-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddNote} className="bg-[#1F3C4F] hover:bg-[#152a38]" data-testid="note-submit-btn">
                      Add Note
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {notes.length > 0 ? (
                <div className="space-y-4">
                  {notes.map((note) => (
                    <div 
                      key={note.id}
                      className="p-4 bg-slate-50 rounded-lg"
                    >
                      <p className="text-slate-700 whitespace-pre-wrap">{note.content}</p>
                      <p className="text-sm text-slate-500 mt-2">
                        {note.user_name} • {new Date(note.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>No notes yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* File Viewer */}
      <FileViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        file={selectedFile}
        files={job?.files || []}
        onNavigate={(file) => setSelectedFile(file)}
      />
    </div>
  );
};

export default JobDetailPage;
